import type { ResolveFactorCandidate } from '../types';
import type { ActivityAutopilotEstimateResponse } from '../types/activity-autopilot';

export function isScope3AutopilotCategory(
  category: string | null | undefined
): category is string {
  if (category == null) return false;
  const num = Number(category);
  return Number.isInteger(num) && num >= 1 && num <= 15;
}

export function buildAutopilotActivityValue(suggestionId: string): string {
  return `autopilot:${suggestionId.trim().slice(0, 89)}`;
}

export function buildAutopilotEstimateParameters(input: {
  methodValues: Record<string, string>;
  methodUnits: Record<string, string>;
  requiredValueKeys: string[];
  requiredUnitKeys: string[];
}): Record<string, number | string> | null {
  const parameters: Record<string, number | string> = {};

  for (const valueKey of input.requiredValueKeys) {
    const parsed = Number(input.methodValues[valueKey]);
    if (!Number.isFinite(parsed) || parsed <= 0) {
      return null;
    }
    parameters[valueKey] = parsed;
  }

  for (const unitKey of input.requiredUnitKeys) {
    const unit = input.methodUnits[unitKey]?.trim();
    if (!unit) {
      return null;
    }
    parameters[unitKey] = unit;
  }

  return parameters;
}

export function mapAutopilotEstimateToResolvedCandidate(input: {
  response: ActivityAutopilotEstimateResponse;
  unitType: string;
  dataVersion?: string;
}): ResolveFactorCandidate | undefined {
  const factor = input.response.estimate.emissionFactor;
  const factorId = factor.id?.trim();
  if (!factorId) return undefined;

  const activityValue = input.response.estimate.activityData.activityValue;
  const co2e = input.response.estimate.co2e;
  const unitType = input.unitType.trim() || factor.unitType || 'Number';
  const firstSourceTrail = input.response.sourceTrail[0];

  return {
    id: factorId,
    activityId: factor.activityId?.trim() ?? '',
    name: factor.name,
    source: factor.source,
    region: factor.region,
    regionName: firstSourceTrail?.regionName ?? factor.regionName ?? factor.region,
    year: factor.year,
    unit: input.response.estimate.activityData.activityUnit || factor.unit,
    unitType,
    co2ePerUnit: Number.isFinite(activityValue) && activityValue > 0 ? co2e / activityValue : 0,
    dataVersion: input.dataVersion?.trim() ?? 'autopilot',
    selectorSource: 'climatiq',
  };
}
